import { useCallback, useMemo, useState } from 'react';
import {useGlobal} from 'reactn';
import { createAnnouncement, createFeed, searchMembersForFeed, updateFeed } from '../../../../../util/APIUtils';
import { debounce } from '@material-ui/core';
import { deduplicateSearchMembersAndGroupLocationsHistory, getAttachmentFiles, getErrorMsg, getUserDataFromType, joinFeedRoom } from '../../services/Feed.service';
import store, { changeToastType, showToast } from '../../../../core/store';
import { USER_CONTEXT } from '../../../../../constants';
import { buildMemberNames, mapCreateResponseToFeed, buildUpdatedPermissions, removeViewOnlyTag, addViewOnlyTag } from '../../mappers/feedUtility';
import { MEMBERSHIP_TYPE } from '../../constants';
import { useAttachmentUploadHook } from '../attachments/useAttachmentUploadHook';

export const useManageFeedsHook = (directMessageProps, tabNavigation, refetchAnnouncements) => {
    const { setConversations, feedDetails, userDetail } = directMessageProps;
    const SEARCH_ITEM_DELAY = 500;
    const DEFAULT_NUMBER_OF_MESSAGES_ALLOWED = 3;
    const [searchedMembers, setSearchMembers] = useState([]);
    const [isMemberSearchLoading, setMemberSearchLoading] = useState(false);
    const [memberCount, setMemberCount] = useState(0);
    const [isManageFeedLoading, setManageFeedLoading] = useState(false);
    const [, setGlobal] = useGlobal();
    const {
        attachmentFiles,
        snackbarError,
        fileInputRef,
        handleFileChange,
        onFileUploadClick,
        onSnackbarClose,
        resetAttachments,
    } = useAttachmentUploadHook();

    const onSearchChangeHandler = (text) => {
        searchItem(text);
    };

    const searchItem = useMemo(
        () =>
            debounce(async (value) => {
                setMemberSearchLoading(true);
                const type = tabNavigation?.activeTab === "Messages" ? 'PERSON' : undefined;
                const memberFeeds = await searchMembersForFeed(value, type);
                memberFeeds.data = deduplicateSearchMembersAndGroupLocationsHistory(memberFeeds);
                setMemberSearchLoading(false);
                if (memberFeeds.status === 200) {
                    setSearchMembers(memberFeeds.data);
                    return;
                }
                store.dispatch(changeToastType("error"));
                store.dispatch(showToast(getErrorMsg(memberFeeds.status)));
            }, SEARCH_ITEM_DELAY),
        [tabNavigation?.activeTab]
    );
    
    const setGlobalLoader = useCallback((isLoading) => {
        setGlobal({
            isLoading
        });
    }, [setGlobal]);

    // Pure helper functions
    const showToastMessage = (type, message) => {
        store.dispatch(changeToastType(type));
        store.dispatch(showToast(message));
    };

    const closeModal = () => document.querySelector(`div[aria-label="close"]`)?.click();

    const onSaveChanges = useCallback(async ({ conversationSettings }) => {
        const { id, name, metadata, permissions: currentPermissions } = feedDetails;
        const { refreshFeedDetails, handleFeedDetailsLoaded } = directMessageProps;
        const { shutDownConversation } = conversationSettings;

        setManageFeedLoading(true);

        const updatedPermissions = buildUpdatedPermissions(conversationSettings, currentPermissions);
        const payload = { name, metadata, permissions: updatedPermissions };

        try {
            const response = await updateFeed(id, USER_CONTEXT.personId, payload);

            if (response?.status !== 200) {
                showToastMessage("error", "Something went wrong!");
                return;
            }

            if (refreshFeedDetails) {
                await refreshFeedDetails(id);
            } else {
                handleFeedDetailsLoaded({ ...feedDetails, permissions: updatedPermissions });
            }

            setConversations?.(prev => prev.map(conv => {
                if (conv.id !== id) return conv;

                const otherTags = removeViewOnlyTag(conv.tags || []);
                const newTags = shutDownConversation
                    ? addViewOnlyTag(otherTags)
                    : otherTags;

                return { ...conv, tags: newTags };
            }));

            showToastMessage("success", "Feed updated successfully!");
            closeModal();

        } catch (error) {
            console.error('[ManageFeeds] onSaveChanges Error:', error);
            showToastMessage("error", "Something went wrong!");
        } finally {
            setManageFeedLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feedDetails, directMessageProps, setConversations]);

    const isLearnerPresent = (selectedMembers) => {
        return selectedMembers.some(member => member.type === 'person' && member.role && member.role.toLowerCase().includes('learner'));
    };

    const onCreateHandler = useCallback(async ({ selectedMembers, conversationSettings, isAttorneyConversation = false, isAdminLearnerConversation = false }) => {
        setManageFeedLoading(true);
        const modifiedSelectedMemberCollection = Object.assign([], selectedMembers);
        // Creator should not be a participant for attorney or admin-learner conversations
        const excludeCreatorFromParticipants = isAttorneyConversation || isAdminLearnerConversation;
        const payload = {
            "feedType": "dm",
            "name": buildMemberNames(selectedMembers, USER_CONTEXT.personId, MEMBERSHIP_TYPE.PERSON,),//getUserDataFromType(modifiedSelectedMemberCollection, 'person', 'recordType').map(item => item.username).join(", "),
            "participantPersonKeys": getUserDataFromType(modifiedSelectedMemberCollection, 'person', 'recordType').map(item => item.id),
            "isConfidential": isAttorneyConversation, // Set to true only for attorney conversations (attorney-client privilege)
            "metadata": {
                "description": "",
                "isAttorneyConversation": isAttorneyConversation,
                "isAdminLearnerConversation": isAdminLearnerConversation
            },
            "permissions": {
                "messaging_disabled": false,
                "learners_can_send_message": conversationSettings.learnerCanSendMessages,
                "learner_can_reply": true,
                "limit_learner_messages": isLearnerPresent(selectedMembers) && conversationSettings.limitLearnerMessages,
                "learner_message_limit": conversationSettings.numberOfMessages
            },
            "participantLocationKeys": getUserDataFromType(modifiedSelectedMemberCollection, 'location', 'type').map(item => item.id),
            "participantGroupKeys": getUserDataFromType(modifiedSelectedMemberCollection, 'learner_group', 'type').map(item => item.id)
        }
        try {
            const createdFeed = await createFeed(payload);
            if (createdFeed.status === 200) {
                store.dispatch(changeToastType("success"));
                store.dispatch(showToast("Feed created successfully!"));

                // For attorney/admin-learner conversations, creator is not a participant, so skip adding to local state and auto-opening
                if (!excludeCreatorFromParticipants) {
                    // Join the feed room to receive real-time messages
                    joinFeedRoom([`feed_${createdFeed.data.id}`]);
                    setConversations(conversations => {
                        const conversationIndex = conversations.findIndex(_conversation => _conversation.id === createdFeed.data.id);
                        if (conversationIndex > -1) {
                            conversations.splice(conversationIndex, 1);
                        }
                        conversations.unshift(mapCreateResponseToFeed(createdFeed.data, userDetail.userId, directMessageProps.openConversationRef));
                        return conversations
                    });
                    document.querySelector(`div[aria-label="close"]`)?.click();
                    setTimeout(() => {
                        document.querySelector(`#feed-${createdFeed.data.id}`)?.click();
                    }, 500);
                } else {
                    // For attorney/admin-learner conversations, just close the modal (creator is not a participant)
                    document.querySelector(`div[aria-label="close"]`)?.click();
                }
                return;
            }
            store.dispatch(changeToastType("error"));
            store.dispatch(showToast("Something went wrong!"));
        } catch (e) {
        } finally {
            setManageFeedLoading(false);
        }
    }, [setConversations, directMessageProps, userDetail]);

    const onFeedSettingEditHandler = useCallback(() => {
        // Handle cases where permissions might be null, undefined, or empty array
        const permissions = feedDetails?.permissions;
        const hasValidPermissions = permissions && typeof permissions === 'object' && !Array.isArray(permissions);

        const initialSettings = {
            "learnersCanSendMessage": hasValidPermissions ? permissions.learners_can_send_message : false,
            "messagingDisabled": hasValidPermissions ? permissions.messaging_disabled : false,
            "learnerCanReply": hasValidPermissions ? permissions.learner_can_reply : true,
            "learnerMessageLimit": hasValidPermissions ? permissions.learner_message_limit : DEFAULT_NUMBER_OF_MESSAGES_ALLOWED,
            "limitLearnerMessages": hasValidPermissions ? permissions.limit_learner_messages : true
        }

        return new Promise((resolve) => {
            setMemberCount(feedDetails?.memberCount);
            resolve(initialSettings);
        });
    }, [feedDetails]);
    
    const onCreateAnnouncement = useCallback(async (data) => {
        const { announcementTitle, editorContent, selectedMembers } = data;
        setManageFeedLoading(true);
        try {
            const payload = {
                subject: announcementTitle,
                body: editorContent,
                personKeys: getUserDataFromType(selectedMembers, 'person', 'recordType').map(item => item.id),
                locationKeys: getUserDataFromType(selectedMembers, 'location', 'type').map(item => item.id),
                groupKeys: getUserDataFromType(selectedMembers, 'group', 'type').map(item => item.id),
                attachments: getAttachmentFiles(attachmentFiles)
            }
            if (attachmentFiles.length > 0) {
                setGlobalLoader(true);
            }
            const announcementResonse = await createAnnouncement(payload);
            if (announcementResonse.status === 200) {
                document.querySelector(`div[aria-label="close"]`)?.click();
                store.dispatch(changeToastType("success"));
                store.dispatch(showToast(announcementResonse.message));

                // Refetch announcements to show the newly created one
                if (refetchAnnouncements) {
                    refetchAnnouncements();
                }
                return;
            }
            store.dispatch(changeToastType("error"));
            store.dispatch(showToast("Something went wrong while trying to create announcement, please try again after few minutes or try reloading the application!"));
        } catch (e) {
            store.dispatch(changeToastType("error"));
            store.dispatch(showToast("Something went wrong while trying to create announcement, please try again after few minutes or try reloading the application!"));
        } finally {
            setGlobalLoader(false);
            resetAttachments();
            setManageFeedLoading(false);
        }
    // attachmentFiles, resetAttachments, setGlobalLoader are from hook/closure; stable refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetchAnnouncements]);
    return {
        isManageFeedLoading,
        memberCount,
        DEFAULT_NUMBER_OF_MESSAGES_ALLOWED,
        searchedMembers,
        isMemberSearchLoading,
        onCreateAnnouncement,
        onSaveChanges,
        onSearchChangeHandler,
        onCreateHandler,
        onFeedSettingEditHandler,
        setSearchMembers,
        // Attachment upload for FeedsNewMessageModal (create message / create announcement)
        attachmentFiles,
        snackbarError,
        fileInputRef,
        handleFileChange,
        onFileUploadClick,
        onSnackbarClose,
        resetAttachments,
    };
};
